import { useRef, useState, useCallback, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";

interface UseSignatureProps {
  onChange?: (signature: string) => void;
  onClear?: () => void;
  /**
   * A data URL to restore onto the canvas on mount (e.g. after expand/collapse).
   * When provided, the signature pad will display this image immediately.
   */
  value?: string | null;
}

export function useSignature({
  onChange,
  onClear,
  value,
}: UseSignatureProps = {}) {
  const signatureRef = useRef<SignatureCanvas>(null);
  const lastCapturedDataUrlRef = useRef<string | null>(value ?? null);
  const [hasSignature, setHasSignature] = useState(Boolean(value));

  // Ref to hold the latest onChange callback so the restore effect can call it
  // without depending on the callback reference (which changes every render
  // when defined inline, causing an infinite re-render loop).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Restore a previous signature onto the canvas when the component mounts
  // (or when value changes from null → data URL, e.g. after expand/collapse remount).
  // Also re-notify onChange so the parent (e.g. SuperDoc field state) knows the
  // field already has a value after the remount.
  useEffect(() => {
    if (!value || !signatureRef.current) return;

    lastCapturedDataUrlRef.current = value;

    const canvas = signatureRef.current.getCanvas();
    signatureRef.current.fromDataURL(value, {
      width: canvas.width,
      height: canvas.height,
    });
    setHasSignature(true);

    // Propagate the restored value back to the parent so SuperDoc's field
    // state is in sync. We already have the data URL — no need to read from
    // the canvas (fromDataURL is async and isEmpty() only tracks pen strokes).
    if (onChangeRef.current) {
      onChangeRef.current(value);
    }
  }, [value]);

  const handleSignatureChange = useCallback(() => {
    if (!signatureRef.current) return;
    const isEmpty = signatureRef.current.isEmpty();
    setHasSignature(!isEmpty);

    if (!isEmpty && onChange) {
      const dataUrl = signatureRef.current.toDataURL();
      lastCapturedDataUrlRef.current = dataUrl;
      onChange(dataUrl);
    }
  }, [onChange]);

  const handleClear = useCallback(() => {
    if (!signatureRef.current) return;

    signatureRef.current.clear();
    lastCapturedDataUrlRef.current = null;
    setHasSignature(false);
    if (onClear) {
      onClear();
    }
  }, [onClear]);

  return {
    signatureRef,
    lastCapturedDataUrlRef,
    hasSignature,
    handleSignatureChange,
    handleClear,
  };
}
